/**
 * useStuckJobs — GET /v1/jobs/stuck (DEC-315 option A).
 *
 * The row types are declared HERE rather than in @lodex-pro/shared on purpose: they are a
 * structural copy of the API's own. Source of truth: packages/api/src/services/stuck-jobs.ts
 * (StuckJobRow). Change both together.
 *
 * The query key starts with 'jobs', so anything that already invalidates ['jobs'] (the
 * cancel modal, the dispatcher-close modal) refreshes this list too.
 */

import { useApiQuery, type useApiOpts } from '@lodex-pro/web-shared';

export interface StuckJobRow {
  readonly jobId: string;
  /** Ticket / display number (jobs.haul_id). */
  readonly haulId: string | null;
  readonly statusCode: string;
  readonly statusLabel: string;
  readonly driverId: string | null;
  readonly driverName: string | null;
  readonly customerName: string | null;
  readonly siteName: string | null;
  /** ISO instant of the last FIELD activity (or the job's last edit when it has no events). */
  readonly lastActivityAt: string;
  readonly lastActivitySource: 'EVENT' | 'JOB_UPDATED_AT';
  readonly thresholdHours: number;
  readonly reason: 'PAUSED_STALE' | 'IN_PROGRESS_STALE';
  readonly clockHead: 'STARTED' | 'PAUSED' | 'RESUMED' | 'STOPPED' | null;
  readonly clockHeadTriggeredBy: string | null;
  readonly clockStopped: boolean;
  /** True when dispatcher-close would accept the call (clock head STARTED/RESUMED/PAUSED). */
  readonly closable: boolean;
  /** True when the job has any haul capture; the API refuses to cancel it (DEC-315 option B). */
  readonly hasCapturedLoad: boolean;
  /** DEC-328 — the latest "ask driver to sync" (ISO), or null if never asked. */
  readonly lastNudgeAt: string | null;
  /** DEC-328 — when the driver checked in after that nudge (ISO); null while waiting. */
  readonly lastNudgeCheckinAt: string | null;
}

export interface StuckJobsResponse {
  readonly jobs: readonly StuckJobRow[];
  readonly inProgressThresholdHours: number;
  readonly generatedAt: string;
}

export const STUCK_JOBS_QUERY_KEY = (hours: number): readonly ['jobs', 'stuck', number] =>
  ['jobs', 'stuck', hours] as const;

export function useStuckJobs(
  apiOpts: ReturnType<typeof useApiOpts>,
  hours = 24,
): ReturnType<typeof useApiQuery<StuckJobsResponse>> {
  return useApiQuery<StuckJobsResponse>(apiOpts, `/v1/jobs/stuck?hours=${hours.toString()}`, {
    queryKey: STUCK_JOBS_QUERY_KEY(hours),
    refetchInterval: 60_000,
  });
}
